/**
 * The room each kind of event starts with.
 *
 * Pure data and one function, so the same rooms are what storage creates, what
 * the chooser's thumbnails draw and what the tests hold to the collision rules:
 * a picture can never promise a room the app does not make.
 *
 * A wedding's is the five tables the server always seeded. The others are
 * spaced for name plates, the canvas's default chair style: a round of ten's
 * plates reach 152 from its centre, so rounds stand 340 apart and no guest's
 * name is printed across a neighbour's. The head table runs across the top,
 * facing the room.
 */

import type { EventKind, Table, TableColor, TableShape } from './types'
import { newId } from './ids'
import type { LocaleCode } from './i18n'
import { planWords } from './planWords'

export interface StarterTable {
  /** A word from `planWords`, or the table's number. */
  name: 'couple' | 'head' | number
  shape: TableShape
  color: TableColor
  x: number
  y: number
  rotation: number
  seats: number
}

const round = (name: number, color: TableColor, x: number, y: number): StarterTable => ({
  name,
  shape: 'round',
  color,
  x,
  y,
  rotation: 0,
  seats: 10,
})

export const STARTER_ROOMS: Record<EventKind, readonly StarterTable[]> = {
  wedding: [
    { name: 'couple', shape: 'sweetheart', color: 'pink', x: 0, y: -260, rotation: 0, seats: 2 },
    round(1, 'blue', -170, 20),
    round(2, 'blue', 170, 20),
    round(3, 'orange', -170, 300),
    round(4, 'orange', 170, 300),
  ],
  // A family feast: two long tables stood end-on to a small head table.
  celebration: [
    { name: 'head', shape: 'sweetheart', color: 'pink', x: 0, y: -300, rotation: 0, seats: 4 },
    { name: 1, shape: 'rectangle', color: 'orange', x: -160, y: 80, rotation: 90, seats: 16 },
    { name: 2, shape: 'rectangle', color: 'orange', x: 160, y: 80, rotation: 90, seats: 16 },
  ],
  // A dinner or a gala: the hosts facing two rows of rounds of ten.
  corporate: [
    { name: 'head', shape: 'sweetheart', color: 'orange', x: 0, y: -300, rotation: 0, seats: 6 },
    round(1, 'blue', -340, -60),
    round(2, 'blue', 0, -60),
    round(3, 'blue', 340, -60),
    round(4, 'blue', -340, 280),
    round(5, 'blue', 0, 280),
    round(6, 'blue', 340, 280),
  ],
  other: [
    round(1, 'blue', -170, -170),
    round(2, 'blue', 170, -170),
    round(3, 'orange', -170, 170),
    round(4, 'orange', 170, 170),
  ],
}

export function starterTables(kind: EventKind, language: LocaleCode): Table[] {
  const words = planWords(language)
  return STARTER_ROOMS[kind].map((table, index) => ({
    id: newId(),
    name:
      table.name === 'couple'
        ? words.couple
        : table.name === 'head'
          ? words.head
          : words.table(table.name),
    shape: table.shape,
    color: table.color,
    x: table.x,
    y: table.y,
    rotation: table.rotation,
    seatCount: table.seats,
    zIndex: index,
    seats: Array.from({ length: table.seats }, (_, position) => ({
      id: newId(),
      position,
      guestId: null,
      label: null,
    })),
  }))
}
